'use client';

/**
 * MobileDrawer.tsx
 *
 * 모바일 내비게이션 드로어(국문). 영문 드로어(EnMobileDrawer)와 같은 형태로 통일했다 —
 * 헤더 아래에서 전폭으로 펼쳐지는 드롭다운, 섹션 아코디언(SectionHeader), 하단 로케일 스위치 + CTA.
 * 내용(서비스/요금/미디어/고객지원 항목·링크·CTA 동선·전화번호)은 국문 그대로다.
 *
 * 접근성: 열릴 때 패널로 포커스 이동, Tab 순환(포커스 트랩), 닫으면 트리거(햄버거)로 복귀,
 * 열려 있는 동안 body 스크롤 잠금.
 */

import { useState, useRef, useEffect, RefObject } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import Icon from '@/components/marketing/primitives/Icon';
import { hasInquirySection, isPlainLeftClick } from '@/components/marketing/inquiry/anchor';
import Btn from '@/components/marketing/primitives/Btn';
import { ICON_MAP } from '@/components/marketing/theme';
import { SERVICES } from '@/components/marketing/data/services';
import { PRICING_SECTIONS, planSectionHref } from '@/components/marketing/data/pricing';
import { MEDIA_SECTIONS } from '@/lib/media-sections';
import { toMainSiteHref } from '@/lib/utils/main-site-href';
import { UserRole } from '@/lib/types/account';
import { authStorage } from '@/lib/storage';
import { queryClient } from '@/lib/query';

interface MobileDrawerProps {
  open: boolean;
  onClose: () => void;
  /** Element that opened the drawer — focus is restored here on close. */
  triggerRef: RefObject<HTMLButtonElement>;
  /** 스티키 헤더의 실제 하단 — 드로어를 그 아래에 맞춘다(HeaderClient 가 측정해 넘긴다). */
  topOffset: number;
  isLogin: boolean;
  userRole?: UserRole;
  /**
   * 로케일 쿠키를 심는다. 마케팅에서는 <a href="/en"> 의 기본 이동이 뒤따르고,
   * 앱(appMode)에서는 훅이 직접 전체 리로드한다. use-locale-switch.ts 참고.
   */
  onSwitchLocale: () => void;
  /** 앱 화면에서 렌더될 때 켠다. HeaderClient 의 같은 prop 참고. */
  appMode?: boolean;
  /**
   * 비로그인 상담 CTA 를 눌렀는데 **이 화면에 하단 상담 폼이 없을 때.** 서랍을 닫고 상담
   * 모달을 여는 일은 HeaderClient 가 한다 — 이 서랍은 닫히면서 스스로 언마운트되므로
   * (`if (!open) return null`) 모달 상태를 여기 두면 열리자마자 같이 사라진다.
   *
   * 폼이 있으면 부르지 않는다. 그때는 앵커가 종전대로 동작한다(`inquiry/anchor`).
   */
  onInquiryFallback: () => void;
  /** 상담 CTA 의 href. HeaderClient 의 같은 prop 참고 — 하이드레이션 전에만 쓰인다. */
  inquiryHref: string;
  /** blog 서브도메인일 때의 메인 호스트 origin. HeaderClient 의 같은 prop 참고. */
  mainOrigin?: string;
  /** 번역되지 않은 화면에서 KO/EN 토글을 숨긴다. HeaderClient 의 같은 prop 참고. */
  hideLocaleSwitch?: boolean;
}

type SectionKey = 'services' | 'plan' | 'media' | 'support';

const DRAWER_LINK_CLS = 'block px-5 py-3 text-body3Medium text-lawkitGray-700 hover:text-lawkitGray-950 hover:bg-lawkitGray-50 transition-colors';

function SectionHeader({ label, expanded, onToggle }: { label: string; expanded: boolean; onToggle: () => void }) {
  return (
    <button type="button" className="w-full flex items-center justify-between px-5 py-4 text-body3Bold text-lawkitGray-950" aria-expanded={expanded} onClick={onToggle}>
      {label}
      <Icon name={ICON_MAP.caretDown} className={`text-[14px] text-lawkitGray-400 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} />
    </button>
  );
}

export default function MobileDrawer({
  open,
  onClose,
  triggerRef,
  topOffset,
  isLogin,
  userRole,
  onSwitchLocale,
  appMode,
  hideLocaleSwitch,
  onInquiryFallback,
  inquiryHref,
  mainOrigin = '',
}: MobileDrawerProps) {
  const [openSection, setOpenSection] = useState<SectionKey | null>(null);
  const wasOpen = useRef(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const router = useRouter();

  const siteHref = (href: string) => toMainSiteHref(mainOrigin, href);
  const myPageLink = siteHref(userRole === 'L' ? '/lawyer' : '/my-contract/dashboard');
  /** 데스크탑 헤더와 같은 규칙 — HeaderClient 의 cta 주석 참고. 비로그인은 링크가 아니라 모달이다. */
  const showInquiryCta = !isLogin && !appMode;

  const handleSignOut = () => {
    authStorage.sessionClear();
    queryClient.clear();
    onClose();
    router.refresh();
  };

  // 열릴 때 패널로 포커스 이동
  useEffect(() => {
    if (open) panelRef.current?.focus();
  }, [open]);

  // 닫히면 트리거(햄버거)로 포커스 복귀
  useEffect(() => {
    if (wasOpen.current && !open) triggerRef.current?.focus();
    wasOpen.current = open;
  }, [open, triggerRef]);

  // 열려 있는 동안 body 스크롤 잠금
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  const toggleSection = (key: SectionKey) => setOpenSection(cur => (cur === key ? null : key));

  /** 간단한 포커스 트랩 — Tab 이 다이얼로그 안에서 순환한다(EnMobileDrawer 와 같은 패턴). */
  const trapFocus = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab' || !panelRef.current) return;
    const focusables = panelRef.current.querySelectorAll<HTMLElement>('a[href], button:not([disabled])');
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return (
    <div className="lg:hidden">
      {/* Backdrop */}
      <div className="fixed inset-x-0 bottom-0 z-40 bg-black/20" style={{ top: topOffset }} onClick={onClose} aria-hidden="true" />

      {/* Panel — 헤더 아래(topOffset)에서 전폭으로 펼쳐진다. */}
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <div
        ref={panelRef}
        tabIndex={-1}
        className="fixed inset-x-0 bottom-0 z-50 bg-white overflow-y-auto outline-none animate-[fadeDown_0.18s_ease-out]"
        style={{ top: topOffset }}
        role="dialog"
        aria-modal="true"
        aria-label="모바일 메뉴"
        onKeyDown={trapFocus}
      >
        {/* 서비스 */}
        <div className="border-b border-lawkitGray-100">
          <SectionHeader label="서비스" expanded={openSection === 'services'} onToggle={() => toggleSection('services')} />
          {openSection === 'services' && (
            <div className="pb-2">
              {SERVICES.map(s => (
                <Link key={s.slug} href={siteHref(`/services/${s.slug}`)} onClick={onClose} className="block px-5 py-2.5 hover:bg-lawkitGray-50 transition-colors">
                  <span className="block text-[11px] font-bold tracking-[0.06em] text-lawkitBlue-70 uppercase mb-0.5">{s.cat}</span>
                  <span className="text-body3Medium text-lawkitGray-700">{s.title}</span>
                </Link>
              ))}
              {/* 요금·고객지원 섹션은 허브(/plan, /faq)가 항목 목록 안에 이미 들어 있어 별도 링크가 없다.
                  서비스는 목록이 상세 5건뿐이라 미디어 센터와 같은 방식으로 허브 링크를 따로 둔다. */}
              <Link href={siteHref('/services')} onClick={onClose} className="block px-5 py-3 text-body3Medium text-lawkitGray-500 hover:text-lawkitGray-950 hover:bg-lawkitGray-50 transition-colors">
                전체 보기
              </Link>
            </div>
          )}
        </div>

        {/* 요금 및 계약 안내 */}
        <div className="border-b border-lawkitGray-100">
          <SectionHeader label="요금 및 계약 안내" expanded={openSection === 'plan'} onToggle={() => toggleSection('plan')} />
          {openSection === 'plan' && (
            <div className="pb-2">
              {PRICING_SECTIONS.map(s => (
                <Link key={s.id} href={siteHref(planSectionHref(s.id))} onClick={onClose} className={DRAWER_LINK_CLS}>
                  {s.label}
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* 미디어 센터 */}
        <div className="border-b border-lawkitGray-100">
          <SectionHeader label="미디어 센터" expanded={openSection === 'media'} onToggle={() => toggleSection('media')} />
          {openSection === 'media' && (
            <div className="pb-2">
              {MEDIA_SECTIONS.map(section => (
                <a key={section.slug} href={`/media/${section.slug}`} onClick={onClose} className="block px-5 py-2.5 hover:bg-lawkitGray-50 transition-colors">
                  <span className="block text-[11px] font-bold tracking-[0.06em] text-lawkitBlue-70 uppercase mb-0.5">{section.englishLabel}</span>
                  <span className="text-body3Medium text-lawkitGray-700">{section.label}</span>
                </a>
              ))}
              <a href="/media" onClick={onClose} className="block px-5 py-3 text-body3Medium text-lawkitGray-500 hover:text-lawkitGray-950 hover:bg-lawkitGray-50 transition-colors">
                전체 보기
              </a>
            </div>
          )}
        </div>

        {/* 고객 지원 */}
        <div className="border-b border-lawkitGray-100">
          <SectionHeader label="고객 지원" expanded={openSection === 'support'} onToggle={() => toggleSection('support')} />
          {openSection === 'support' && (
            <div className="pb-2">
              <Link href={siteHref('/faq')} onClick={onClose} className={DRAWER_LINK_CLS}>
                FAQ
              </Link>
              <Link href={siteHref('/introduce')} onClick={onClose} className={DRAWER_LINK_CLS}>
                LawKit 소개
              </Link>
              <Link href={siteHref('/company')} onClick={onClose} className={DRAWER_LINK_CLS}>
                법무법인 오킴스 기업자문팀 & 글로벌 비즈니스 센터
              </Link>
              <Link href={siteHref('/partners')} onClick={onClose} className={DRAWER_LINK_CLS}>
                협력사
              </Link>
            </div>
          )}
        </div>

        {/* 언어 전환 — 마케팅은 <a>로 /en 트리로 전체 로드, 앱은 쿠키+리로드(URL 하나) */}
        {!hideLocaleSwitch && (
          <div className="flex items-center gap-2 px-5 py-4 border-b border-lawkitGray-100 text-[13px] font-semibold">
            <Icon name="ri-global-line" className="text-[15px] text-lawkitGray-400" />
            <span className="text-lawkitGray-950">KO</span>
            <span className="text-lawkitGray-300" aria-hidden="true">
              /
            </span>
            {appMode ? (
              <button type="button" lang="en" onClick={onSwitchLocale} className="text-lawkitGray-600 hover:text-lawkitGray-950 transition-colors" aria-label="View in English">
                EN
              </button>
            ) : (
              <a href={siteHref('/en')} lang="en" hrefLang="en" onClick={onSwitchLocale} className="text-lawkitGray-600 hover:text-lawkitGray-950 transition-colors" aria-label="View in English">
                EN
              </a>
            )}
          </div>
        )}

        {/* Auth / CTA — 데스크탑 헤더와 같은 구성/규칙(로그인 O → 빠른 접수 / 로그아웃 + 마케팅 → 상담 폼). */}
        <div className="px-5 py-5 flex flex-col gap-3">
          {isLogin ? (
            <>
              <Btn to={siteHref('/quick-intake')} variant="primary" size="md" className="w-full" onClick={onClose}>
                상담 신청
                <Icon name={ICON_MAP.arrowRight} className="text-[16px]" />
              </Btn>
              <Link
                href={myPageLink}
                onClick={onClose}
                className="inline-flex items-center justify-center gap-1.5 py-2 text-body3Medium text-lawkitGray-700 hover:text-lawkitGray-950 transition-colors"
              >
                <Icon name="ri-user-line" className="text-[16px]" />
                마이페이지
              </Link>
              <button
                type="button"
                onClick={handleSignOut}
                className="inline-flex items-center justify-center gap-1.5 py-2 text-body3Medium text-lawkitGray-500 hover:text-lawkitGray-950 transition-colors"
              >
                <Icon name="ri-logout-box-r-line" className="text-[16px]" />
                로그아웃
              </button>
            </>
          ) : (
            <>
              <div className="flex gap-2">
                <Link
                  href={siteHref('/sign-in')}
                  onClick={onClose}
                  className="flex-1 flex items-center justify-center py-2.5 rounded-lg border border-lawkitGray-200 text-label1Medium text-lawkitGray-700 hover:bg-lawkitGray-50 transition-colors"
                >
                  로그인
                </Link>
                <Link
                  href={siteHref('/sign-up')}
                  onClick={onClose}
                  className="flex-1 flex items-center justify-center py-2.5 rounded-lg bg-lawkitBlue-70 text-label1Medium text-white hover:opacity-90 transition-opacity"
                >
                  회원가입
                </Link>
              </div>
              {showInquiryCta && (
                <>
                  {/* 하단 폼이 있으면 앵커 그대로(서랍만 닫는다), 없으면 모달 — onInquiryFallback 주석. */}
                  <Btn
                    href={inquiryHref}
                    variant="primary"
                    size="md"
                    className="w-full"
                    onClick={e => {
                      // 새 탭으로 열려는 클릭은 링크 그대로 둔다 — 서랍도 닫지 않는다.
                      if (!isPlainLeftClick(e)) return;
                      if (hasInquirySection()) {
                        onClose();
                        return;
                      }
                      e.preventDefault();
                      onInquiryFallback();
                    }}
                  >
                    상담 신청
                    <Icon name={ICON_MAP.arrowRight} className="text-[16px]" />
                  </Btn>
                  <p className="text-center text-captionRegular text-lawkitGray-500">평일 09:00-18:00 · 010-9731-5886</p>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
